import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../components/ui/sheet";
import { Plus, Search, Pencil, Trash2, Mail, Phone, MapPin, Loader2, Merge } from "lucide-react";
import { toast } from "sonner";
import { formatDate } from "../lib/format";

const EMPTY = {
  name: "",
  nif_cif: "",
  address: "",
  city: "",
  postal_code: "",
  country: "Espanya",
  phone: "",
  email: "",
  notes: "",
  tags: [],
};

export default function Clients() {
  const { t, lang } = useI18n();
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deduping, setDeduping] = useState(false);

  const load = (search) => {
    api.get("/clients", { params: search ? { q: search } : {} }).then((r) => setClients(r.data));
  };

  useEffect(() => {
    load();
  }, []);

  const onSearch = (e) => {
    setQ(e.target.value);
    load(e.target.value);
  };

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({ ...EMPTY, ...c, tags: c.tags || [] });
    setOpen(true);
  };

  const save = async (force = false) => {
    if (saving) return; // hard guard against double-clicks
    setSaving(true);
    try {
      const payload = { ...form, tags: form.tags || [] };
      if (editing) {
        await api.put(`/clients/${editing.id}`, payload);
      } else {
        await api.post("/clients", payload, { params: force ? { force: true } : {} });
      }
      toast.success(t("settings.saved"));
      setOpen(false);
      setEditing(null);
      setForm(EMPTY);
      load(q);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.code === "duplicate_nif") {
        const yes = window.confirm(
          lang === "es"
            ? `${detail.message}\n\n¿Crear de todos modos un nuevo cliente con el mismo NIF?`
            : `${detail.message}\n\nVols crear igualment un nou client amb el mateix NIF?`
        );
        if (yes) {
          setSaving(false);
          await save(true);
          return;
        }
      } else {
        toast.error(typeof detail === "string" ? detail : (detail?.message || "Error"));
      }
    } finally {
      setSaving(false);
    }
  };

  const dedupe = async () => {
    if (!window.confirm(
      lang === "es"
        ? "Esto fusionará clientes duplicados (mismo NIF) reasignando sus presupuestos/facturas. ¿Continuar?"
        : "Això fusionarà clients duplicats (mateix NIF) reassignant els seus pressupostos/factures. Continuar?"
    )) return;
    setDeduping(true);
    try {
      const r = await api.post("/clients/dedupe");
      toast.success(
        lang === "es"
          ? `${r.data.removed} duplicado${r.data.removed === 1 ? "" : "s"} eliminado${r.data.removed === 1 ? "" : "s"}`
          : `${r.data.removed} duplicat${r.data.removed === 1 ? "" : "s"} eliminat${r.data.removed === 1 ? "" : "s"}`
      );
      load(q);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setDeduping(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(t("clients.delete_confirm"))) return;
    await api.delete(`/clients/${c.id}`);
    toast.success("Eliminat");
    load(q);
  };

  // Count duplicate clients (same NIF, grouping size minus 1 per group). This
  // surfaces the "Fusionar duplicats" button only when there's something to do.
  const dupCount = (() => {
    const groups = {};
    for (const c of clients) {
      const nif = (c.nif_cif || "").trim();
      if (!nif) continue;
      groups[nif] = (groups[nif] || 0) + 1;
    }
    let n = 0;
    for (const k in groups) if (groups[k] > 1) n += groups[k] - 1;
    return n;
  })();
  const dupNifs = new Set(
    Object.entries(
      clients.reduce((acc, c) => {
        const k = (c.nif_cif || "").trim();
        if (!k) return acc;
        acc[k] = (acc[k] || 0) + 1;
        return acc;
      }, {})
    ).filter(([, v]) => v > 1).map(([k]) => k)
  );

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tighter">
            {t("clients.title")}
          </h1>
          <p className="text-sm text-slate-400 mt-1">{clients.length} {t("dashboard.clients").toLowerCase()}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {dupCount > 0 && (
            <Button
              variant="outline"
              onClick={dedupe}
              disabled={deduping}
              data-testid="clients-dedupe-btn"
              className="border-amber-500/40 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20"
            >
              {deduping
                ? <Loader2 size={14} className="mr-2 animate-spin" />
                : <Merge size={14} className="mr-2" />}
              {lang === "es"
                ? `Fusionar ${dupCount} duplicado${dupCount === 1 ? "" : "s"}`
                : `Fusionar ${dupCount} duplicat${dupCount === 1 ? "" : "s"}`}
            </Button>
          )}
          <Button
            onClick={openNew}
            data-testid="new-client-btn"
            className="bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400"
          >
            <Plus size={16} className="mr-2" />
            {t("clients.new")}
          </Button>
        </div>
      </div>

      <div className="relative max-w-md">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <Input
          data-testid="clients-search"
          value={q}
          onChange={onSearch}
          placeholder={t("clients.search")}
          className="bg-[#05070D] border-white/10 pl-9 h-11"
        />
      </div>

      {clients.length === 0 ? (
        <div className="card-surface p-12 text-center text-slate-500">
          {t("clients.empty")}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {clients.map((c) => (
            <div
              key={c.id}
              className="card-surface p-5 hover:border-blue-500/30 transition-all group"
              data-testid={`client-card-${c.id}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="min-w-0">
                  <h3 className="font-semibold text-base truncate">{c.name}</h3>
                  {c.nif_cif && (
                    <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1.5">
                      <span>{c.nif_cif}</span>
                      {dupNifs.has((c.nif_cif || "").trim()) && (
                        <span
                          data-testid={`client-dup-badge-${c.id}`}
                          className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-200 border border-amber-500/40 font-semibold uppercase tracking-wider"
                        >
                          {lang === "es" ? "Duplicado" : "Duplicat"}
                        </span>
                      )}
                    </p>
                  )}
                </div>
                <div
                  className="flex items-center gap-1 transition-opacity
                             [@media(hover:hover)]:opacity-0
                             [@media(hover:hover)]:group-hover:opacity-100"
                >
                  <button
                    type="button"
                    onClick={() => openEdit(c)}
                    data-testid={`client-edit-${c.id}`}
                    aria-label={t("clients.edit")}
                    className="p-2 rounded-lg bg-white/5 [@media(hover:hover)]:bg-transparent hover:bg-white/10 text-slate-300 [@media(hover:hover)]:text-slate-400 hover:text-blue-400 touch-manipulation"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(c)}
                    data-testid={`client-delete-${c.id}`}
                    aria-label="Eliminar"
                    className="p-2 rounded-lg bg-white/5 [@media(hover:hover)]:bg-transparent hover:bg-rose-500/15 text-slate-300 [@media(hover:hover)]:text-slate-400 hover:text-rose-400 touch-manipulation"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              <div className="space-y-1.5 text-xs text-slate-400">
                {c.email && (
                  <div className="flex items-center gap-1.5 truncate">
                    <Mail size={11} /> <span className="truncate">{c.email}</span>
                  </div>
                )}
                {c.phone && (
                  <div className="flex items-center gap-1.5"><Phone size={11} /> {c.phone}</div>
                )}
                {(c.city || c.country) && (
                  <div className="flex items-center gap-1.5 truncate">
                    <MapPin size={11} /> <span className="truncate">{[c.city, c.country].filter(Boolean).join(", ")}</span>
                  </div>
                )}
              </div>
              {(c.tags || []).length > 0 && (
                <div className="flex flex-wrap gap-1 mt-3">
                  {c.tags.map((tag, i) => (
                    <span key={i} className="text-[10px] px-2 py-0.5 rounded-full border border-blue-500/30 bg-blue-500/10 text-blue-300">
                      {tag}
                    </span>
                  ))}
                </div>
              )}
              <div className="text-[10px] text-slate-600 mt-3">
                {formatDate(c.created_at, lang)}
              </div>
            </div>
          ))}
        </div>
      )}

      <Sheet open={open} onOpenChange={(v) => { if (!saving && !deleting) setOpen(v); }}>
        <SheetContent
          className="glass border-white/10 w-full sm:max-w-lg max-w-full overflow-y-auto p-4 sm:p-6"
          data-testid="client-form-sheet"
        >
          <SheetHeader>
            <SheetTitle className="font-display text-xl tracking-tight pr-8">
              {editing ? t("clients.edit") : t("clients.new")}
            </SheetTitle>
          </SheetHeader>
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 pb-24">
            {[
              { k: "name", required: true, full: true },
              { k: "nif_cif" },
              { k: "email", type: "email" },
              { k: "phone" },
              { k: "country" },
              { k: "address", full: true },
              { k: "city" },
              { k: "postal_code" },
            ].map((f) => (
              <div key={f.k} className={f.full ? "sm:col-span-2" : ""}>
                <label className="text-xs uppercase tracking-wider text-slate-400 block mb-1.5">
                  {t(`clients.${f.k === "postal_code" ? "postal_code" : f.k}`)}
                  {f.required && <span className="text-rose-400 ml-1">*</span>}
                </label>
                <Input
                  data-testid={`client-input-${f.k}`}
                  type={f.type || "text"}
                  value={form[f.k] || ""}
                  onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                  className="bg-[#05070D] border-white/10 h-11"
                />
              </div>
            ))}
            <div className="sm:col-span-2">
              <label className="text-xs uppercase tracking-wider text-slate-400 block mb-1.5">
                {t("clients.tags")} (separades per coma)
              </label>
              <Input
                data-testid="client-input-tags"
                value={(form.tags || []).join(", ")}
                onChange={(e) =>
                  setForm({
                    ...form,
                    tags: e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
                  })
                }
                className="bg-[#05070D] border-white/10 h-11"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs uppercase tracking-wider text-slate-400 block mb-1.5">
                {t("clients.notes")}
              </label>
              <Textarea
                data-testid="client-input-notes"
                value={form.notes || ""}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="bg-[#05070D] border-white/10 min-h-[80px]"
              />
            </div>
          </div>
          <div className="flex flex-col-reverse sm:flex-row gap-2 pt-5 sticky bottom-0 bg-gradient-to-t from-[#05070D] via-[#05070D]/95 to-transparent pb-1 -mx-4 sm:-mx-6 px-4 sm:px-6">
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              data-testid="client-cancel-btn"
              disabled={saving || deleting}
              className="border-white/10 h-11"
            >
              {t("clients.cancel")}
            </Button>
            {editing && (
              <Button
                variant="outline"
                disabled={saving || deleting}
                onClick={async () => {
                  if (!window.confirm(t("clients.delete_confirm"))) return;
                  setDeleting(true);
                  try {
                    await api.delete(`/clients/${editing.id}`);
                    toast.success("Eliminat");
                    setOpen(false);
                    setEditing(null);
                    setForm(EMPTY);
                    load(q);
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Error");
                  } finally {
                    setDeleting(false);
                  }
                }}
                data-testid="client-delete-btn"
                className="border-rose-500/40 bg-rose-500/10 text-rose-200 hover:bg-rose-500/20 h-11"
              >
                {deleting
                  ? <Loader2 size={14} className="mr-2 animate-spin" />
                  : <Trash2 size={14} className="mr-2" />}
                {t("clients.delete") || "Eliminar"}
              </Button>
            )}
            <Button
              onClick={() => save(false)}
              data-testid="client-save-btn"
              disabled={!form.name || saving || deleting}
              className="flex-1 h-11 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 disabled:opacity-60"
            >
              {saving && <Loader2 size={14} className="mr-2 animate-spin" />}
              {t("clients.save")}
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
